import { ThreeEvent, ThreeEvents } from '@/renderer/types';
import { RootState, AppDispatch } from '@/store';
import { createListenerMiddleware } from '@reduxjs/toolkit';
import { ProjectRenderer } from '@/renderer/ProjectRenderer';
import { creatorActions } from '../slices/creatorSlice';
import { AttributeValue } from '@/api/AttributeValueApi';
import { CreatorAction } from '@/types/types';
import { saveProjectToLocal } from '@/utils/utilities';
import CreatorProductModuleApi, {
	CreatorProductModule,
} from '@/api/CreatorProductModuleApi';
import ProductModuleCollectionApi from '@/api/ProductModuleCollectionApi';
import ProjectApi from '@/api/ProjectApi';
import PresetApi from '@/api/PresetApi';

const renderer = ProjectRenderer.getInstance();

export const creatorListenerMiddleware = createListenerMiddleware();

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.addCreatorProductModule,
	effect: async ({ payload }, listenerApi) => {
		const state = listenerApi.getState().creator;

		const creatorProductModules = state.options.isPresetMode
			? state.preset.creatorProductModules
			: state.project.creatorProductModules;

		if (creatorProductModules.length === 1)
			await renderer.startCollection(payload);
		else await renderer.addSofaModule(payload);

		for (const attributeValue of payload.selectedAttributeValues) {
			const actions = attributeValue.actions;
			if (!actions.length) continue;

			renderer.handleActions(actions, payload.id, true);
		}

		listenerApi.dispatch(creatorActions.refreshAPIData());
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.handleCreatorProductModuleSwap,
	effect: async ({ payload }, listenerApi) => {
		const state = listenerApi.getState().creator;

		const creatorProductModules = state.options.isPresetMode
			? state.preset.creatorProductModules
			: state.project.creatorProductModules;

		const creatorProductModule = creatorProductModules.find(
			(mod) => mod.id === payload.creatorProductModuleId
		);
		if (!creatorProductModule) return;

		await renderer.moduleManager.swapSofaModule(
			payload.creatorProductModuleId,
			payload.productModule
		);

		for (const attributeValue of creatorProductModule.selectedAttributeValues) {
			const actions = attributeValue.actions;
			if (!actions.length) continue;

			renderer.handleActions(actions, creatorProductModule.id, true);
		}

		listenerApi.dispatch(creatorActions.closeContextMenu());
		listenerApi.dispatch(creatorActions.refreshAPIData());
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.removeCreatorProductModule,
	effect: ({ payload }, listenerApi) => {
		renderer.removeSofaModule(payload);
		listenerApi.dispatch(creatorActions.refreshAPIData());
		listenerApi.dispatch(creatorActions.closeContextMenu());
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.handleCreatorProductModuleConfiguration,
	effect: async ({ payload }, listenerApi) => {
		listenerApi.cancelActiveListeners();
		await listenerApi.delay(250);

		const { selectedCreatorProductModule } =
			listenerApi.getState().creator.options;
		if (!selectedCreatorProductModule) return;

		CreatorProductModuleApi.update({
			id: selectedCreatorProductModule.id,
			selectedAttributeValues:
				selectedCreatorProductModule.selectedAttributeValues.map(
					(attrVal) =>
						({
							id: attrVal.id,
						}) as AttributeValue
				),
		}).then(() => {
			listenerApi.dispatch(creatorActions.refreshAPIData());
		});

		const actions = payload.attributeValues.reduce<CreatorAction[]>(
			(acc, curr) => {
				acc.push(...curr.actions);
				return acc;
			},
			[]
		);

		renderer.handleActions(actions, selectedCreatorProductModule.id);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.handleCollectionAttributeValuesChange,
	effect: async ({ payload }, listenerApi) => {
		listenerApi.cancelActiveListeners();
		await listenerApi.delay(500);
		const state = listenerApi.getState().creator;

		const { creatorProductModules } = state.options.isPresetMode
			? state.preset
			: state.project;

		Promise.all(
			creatorProductModules.map((creaProdMod) =>
				CreatorProductModuleApi.update({
					id: creaProdMod.id,
					selectedAttributeValues: creaProdMod.selectedAttributeValues.map(
						(attrVal) =>
							({
								id: attrVal.id,
							}) as AttributeValue
					),
				})
			)
		).then(() => {
			listenerApi.dispatch(creatorActions.refreshAPIData());
		});
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.undo,
	effect: async ({ payload }, listenerApi) => {
		listenerApi.cancelActiveListeners();
		await listenerApi.delay(500);

		const state = listenerApi.getState().creator;

		const { project, preset } = listenerApi.getState().creator;

		state.options.isPresetMode
			? await PresetApi.update({
					id: preset.id,
					creatorProductModules: preset.creatorProductModules.map(
						(creaProdMod) =>
							({
								id: creaProdMod.id,
								selectedAttributeValues:
									creaProdMod.selectedAttributeValues.map(
										(attrVal) =>
											({
												id: attrVal.id,
											}) as AttributeValue
									),
							}) as CreatorProductModule
					),
				})
			: await ProjectApi.update({
					id: project.id,
					creatorProductModules: project.creatorProductModules.map(
						(creaProdMod) =>
							({
								id: creaProdMod.id,
								selectedAttributeValues:
									creaProdMod.selectedAttributeValues.map(
										(attrVal) =>
											({
												id: attrVal.id,
											}) as AttributeValue
									),
							}) as CreatorProductModule
					),
				});

		listenerApi.dispatch(creatorActions.refreshAPIData());
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.createFromPreset,
	effect: async ({ payload }, listenerApi) => {
		const serializedRenderer = structuredClone(payload.preset.options.renderer);
		if (!serializedRenderer) return;

		for (const sofaModuleData of serializedRenderer.modulesManager
			.sofaModules) {
			const newModule = payload.project.creatorProductModules.find(
				(mod) => mod.refId === sofaModuleData.id
			);

			if (!newModule) continue;
			sofaModuleData.id = newModule.id;
		}

		renderer.collectionBuilder.buildCollection(serializedRenderer);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.syncProject,
	effect: ({ payload }, listenerApi) => {
		saveProjectToLocal(listenerApi.getState().creator, renderer.export());
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.loadProject,
	effect: async ({ payload }, listenerApi) => {
		if (!payload.selectedProductModuleCollection) return;

		renderer.collectionBuilder.buildCollection(payload.projectRenderer);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.loadPreset,
	effect: async ({ payload }, listenerApi) => {
		if (!payload.options.renderer || !payload.productModuleCollection) return;

		renderer.collectionBuilder.buildCollection(payload.options.renderer);

		const collection = await ProductModuleCollectionApi.mutationGetOne(
			payload.productModuleCollection.id
		);

		listenerApi.dispatch(creatorActions.selectCollection(collection));
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.handleRendererEvent,
	effect: async ({ payload }, listenerApi) => {
		const { type } = payload;

		switch (type) {
			case ThreeEvent.SLOT_SELECTION:
				break;
			case ThreeEvent.MODULE_SELECTION:
				break;
			case ThreeEvent.DESELECT_ALL:
				break;
			case ThreeEvent.UPDATE_CAMERA_DISTANCE:
				break;
			case ThreeEvent.SET_CREATOR_PRODUCT_MODULES_ORDER: {
				const data =
					payload.data as ThreeEvents[ThreeEvent.SET_CREATOR_PRODUCT_MODULES_ORDER];

				ProjectApi.setCreatorProductModulesOrder({
					projectId: listenerApi.getState().creator.project.id,
					listOrder: data,
				});
				break;
			}
			case ThreeEvent.CONTEXT_MENU:
				break;

			default:
				throw new Error('UNKNOW_EVENT');
		}
	},
});
